import React, { useState, useRef, useEffect } from 'react'
import { SEO, createStructuredData } from '../components/SEO'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ContentWrapper } from '../components/ContentWrapper'
import { 
  InformationCircleIcon, 
  ExclamationTriangleIcon, 
  LightBulbIcon, 
  ArrowTrendingDownIcon 
} from '@heroicons/react/24/outline'
import { 
  BanknotesIcon,
  HomeIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { Fragment } from 'react'
import { Popover, Transition, Disclosure } from '@headlessui/react'
import { Helmet } from 'react-helmet-async'

const schema = z.object({
  monthlyIncome: z.string().refine(value => {
    const numericValue = parseCurrencyInput(value);
    return numericValue > 0;
  }, { message: "Monthly income must be greater than 0" }),
  
  carPayment: z.string().refine(value => {
    const numericValue = parseCurrencyInput(value);
    return numericValue >= 0;
  }, { message: "Car payment cannot be negative" }),
  
  studentLoans: z.string().refine(value => {
    const numericValue = parseCurrencyInput(value);
    return numericValue >= 0;
  }, { message: "Student loans cannot be negative" }),
  
  creditCards: z.string().refine(value => {
    const numericValue = parseCurrencyInput(value);
    return numericValue >= 0;
  }, { message: "Credit card minimum payments cannot be negative" }),
  
  otherDebts: z.string().refine(value => {
    const numericValue = parseCurrencyInput(value);
    return numericValue >= 0;
  }, { message: "Other debts cannot be negative" }),
  
  homeInsurance: z.string().refine(value => {
    const numericValue = parseCurrencyInput(value);
    return numericValue >= 0;
  }, { message: "Home insurance cannot be negative" }),
  
  hoaFees: z.string().refine(value => {
    const numericValue = parseCurrencyInput(value);
    return numericValue >= 0;
  }, { message: "HOA fees cannot be negative" }),
  
  propertyTaxRate: z.number().min(0, 'Property tax rate must be 0 or greater'),
  interestRate: z.number().min(0, 'Interest rate must be 0 or greater'),
  loanTerm: z.number().min(1, 'Loan term is required'),
  loanType: z.string().min(1, 'Loan type is required'),
  creditScore: z.number().min(300, 'Credit score must be 300 or greater'),
})

type FormData = z.infer<typeof schema>

type ResultsType = {
  maxHomePrice: number;
  frontEndDTI: number;
  backEndDTI: number;
  monthlyIncome: number;
  totalMonthlyDebts: number;
  maxMonthlyPayment: number;
  debtImpact: {
    currentMaxHomePrice: number;
    reducedDebtsMaxHomePrice: number;
    debtCapacityReduction: number;
  };
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount)
}

// Utility function for comma-separated currency formatting
const formatCurrencyInput = (value: string) => {
  // Remove non-numeric characters
  const numericValue = value.replace(/[^\d]/g, '');
  
  // If empty, return empty string
  if (!numericValue) return '';
  
  // Convert to number and format with commas
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: 0
  }).format(parseInt(numericValue, 10));
};

// Utility function to parse formatted currency back to number
const parseCurrencyInput = (value: string) => {
  return parseInt(value.replace(/[^\d]/g, ''), 10) || 0;
};

const structuredData = createStructuredData({
  "@type": "WebApplication",
  "name": "Debt-to-Income (DTI) Ratio Calculator",
  "url": "https://calculators.northsidelegal.com/dti-calculator",
  "description": "Calculate your debt-to-income ratio to understand mortgage qualification potential",
  "applicationCategory": "Financial Calculator",
  "operatingSystem": "Any modern web browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "featureList": [
    "Instant DTI calculation",
    "Mortgage qualification insights",
    "Easy-to-use interface"
  ]
})

export default function DTICalculator() {
  const [results, setResults] = useState<null | ResultsType>(null)
  const [showDTIGuide, setShowDTIGuide] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      propertyTaxRate: 2, // Illinois average
      homeInsurance: '2,500', // Updated annual estimate
      interestRate: 6.5,
      loanTerm: 30,
      monthlyIncome: '5,000',
      carPayment: '0',
      studentLoans: '0',
      creditCards: '0',
      otherDebts: '0',
      hoaFees: '0',
      loanType: 'conventional',
      creditScore: 700,
    },
  })

  useEffect(() => {
    if (results && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [results]);

  useEffect(() => {
    // Format default values with commas on component mount
    if (watch('monthlyIncome')) {
      setValue('monthlyIncome', formatCurrencyInput(watch('monthlyIncome')));
    }
    if (watch('homeInsurance')) {
      setValue('homeInsurance', formatCurrencyInput(watch('homeInsurance')));
    }
  }, []);

  const calculateMonthlyPayment = (principal: number, annualRate: number, years: number) => {
    const monthlyRate = annualRate / 1200
    const numberOfPayments = years * 12
    return (
      (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1)
    )
  }

  const getLoanTypeSpecificDTILimits = (
    loanType: 'conventional' | 'fha' | 'va' | 'jumbo', 
    creditScore: number
  ) => {
    switch (loanType) {
      case 'conventional':
        if (creditScore >= 740) {
          return { frontEndMax: 0.36, backEndMax: 0.45 }
        } else if (creditScore >= 700) {
          return { frontEndMax: 0.33, backEndMax: 0.43 }
        } else if (creditScore >= 620) {
          return { frontEndMax: 0.28, backEndMax: 0.36 }
        }
        return { frontEndMax: 0.25, backEndMax: 0.33 }

      case 'fha':
        if (creditScore >= 580) {
          return { frontEndMax: 0.31, backEndMax: 0.43 }
        }
        return { frontEndMax: 0.29, backEndMax: 0.41 }

      case 'va':
        // VA loans are typically more flexible
        return { frontEndMax: 0.41, backEndMax: 0.41 }

      case 'jumbo':
        if (creditScore >= 700) {
          return { frontEndMax: 0.38, backEndMax: 0.43 }
        }
        return { frontEndMax: 0.33, backEndMax: 0.38 }

      default:
        return { frontEndMax: 0.28, backEndMax: 0.36 }
    }
  }

  const getCreditScoreImpact = (creditScore: number) => {
    if (creditScore >= 740) {
      return {
        rateAdjustment: -0.5, // Lower rates
        ltvAdjustment: 0.05 // More lenient LTV
      }
    } else if (creditScore >= 720) {
      return {
        rateAdjustment: -0.25,
        ltvAdjustment: 0.03
      }
    } else if (creditScore >= 700) {
      return {
        rateAdjustment: 0,
        ltvAdjustment: 0
      }
    } else if (creditScore >= 680) {
      return {
        rateAdjustment: 0.25,
        ltvAdjustment: -0.03
      }
    } else if (creditScore >= 620) {
      return {
        rateAdjustment: 0.5,
        ltvAdjustment: -0.05
      }
    } else {
      return {
        rateAdjustment: 1,
        ltvAdjustment: -0.1
      }
    }
  }

  const calculateMaxHomePrice = (
    maxMonthlyPayment: number,
    loanType: string,
    creditScore: number,
    params: {
      interestRate: number,
      loanTerm: number,
      propertyTaxRate: number,
      homeInsurance: number,
      hoaFees: number,
      monthlyDebts?: number  // Optional parameter for existing monthly debts
    }
  ) => {
    // Get base interest rate adjustment based on credit score
    const creditScoreImpact = getCreditScoreImpact(creditScore);
    
    // Adjust interest rate based on credit score
    const effectiveRate = (params.interestRate + creditScoreImpact.rateAdjustment) / 100 / 12; // Monthly rate
    
    // Calculate monthly costs that aren't principal and interest
    const monthlyHOA = params.hoaFees;
    const annualTax = params.propertyTaxRate / 100;  // Convert percentage to decimal
    const annualInsurance = params.homeInsurance;
    
    // Adjust maximum monthly payment based on existing debts
    let adjustedMaxMonthlyPayment = maxMonthlyPayment;
    if (params.monthlyDebts && params.monthlyDebts > 0) {
      // Reduce available payment by existing monthly debts
      adjustedMaxMonthlyPayment = Math.max(0, maxMonthlyPayment - params.monthlyDebts);
    }
    
    // Calculate maximum loan amount using mortgage payment formula
    // P = L[c(1 + c)^n]/[(1 + c)^n - 1]
    // where: P = payment, L = loan amount, c = monthly interest rate, n = number of payments
    const numberOfPayments = params.loanTerm * 12;
    
    // Subtract fixed monthly costs from max payment to get P&I payment
    const maxPIPayment = adjustedMaxMonthlyPayment - monthlyHOA;
    
    // Calculate maximum loan amount
    let maxLoanAmount;
    if (effectiveRate === 0) {
      maxLoanAmount = maxPIPayment * numberOfPayments;
    } else {
      maxLoanAmount = maxPIPayment * 
        (Math.pow(1 + effectiveRate, numberOfPayments) - 1) / 
        (effectiveRate * Math.pow(1 + effectiveRate, numberOfPayments));
    }
    
    // Factor in property tax and insurance
    const yearlyTaxAndInsurance = (maxLoanAmount * annualTax) + annualInsurance;
    const monthlyTaxAndInsurance = yearlyTaxAndInsurance / 12;
    
    // Adjust loan amount to account for tax and insurance
    maxLoanAmount = maxLoanAmount * (1 - (monthlyTaxAndInsurance / adjustedMaxMonthlyPayment));
    
    // Apply loan-to-value ratio based on loan type
    const ltv = loanType === 'fha' ? 0.965 : 0.80; // 96.5% for FHA, 80% for conventional
    
    // Calculate final maximum home price
    const maxHomePrice = maxLoanAmount / ltv;
    
    return Math.max(0, Math.round(maxHomePrice));
  }

  const calculateDebtImpact = (
    monthlyDebts: number, 
    monthlyIncome: number, 
    maxHousingPayment: number,
    loanType: string,
    creditScore: number,
    params: {
      interestRate: number,
      loanTerm: number,
      propertyTaxRate: number,
      homeInsurance: number,
      hoaFees: number
    }
  ) => {
    // Calculate maximum home price with current debts
    const currentMaxHomePrice = calculateMaxHomePrice(
      maxHousingPayment,
      loanType,
      creditScore,
      {
        ...params,
        monthlyDebts
      }
    );

    // Calculate maximum home price without debts
    const maxHomePriceWithoutDebts = calculateMaxHomePrice(
      maxHousingPayment + monthlyDebts,
      loanType,
      creditScore,
      params
    );

    return {
      currentMaxHomePrice,
      reducedDebtsMaxHomePrice: maxHomePriceWithoutDebts,
      debtCapacityReduction: monthlyDebts
    };
  };

  const generateKeyInsights = (results: ResultsType) => {
    const { 
      monthlyIncome = 0, 
      maxMonthlyPayment = 0, 
      monthlyDebts = 0, 
      backEndDTI = 0,
    } = results;

    const insights: string[] = [];

    // Ensure numeric values
    const safeMonthlyIncome = Number(monthlyIncome);
    const safeMaxMonthlyPayment = Number(maxMonthlyPayment);
    const safeMonthlyDebts = Number(monthlyDebts);

    // Monthly mortgage payment insight
    if (safeMaxMonthlyPayment > 0) {
      insights.push(`Your monthly income of ${formatCurrency(safeMonthlyIncome)} allows for a maximum monthly mortgage payment of ${formatCurrency(safeMaxMonthlyPayment)}.`);
    } else {
      insights.push(`Your monthly income of ${formatCurrency(safeMonthlyIncome)} suggests potential for a mortgage payment, but exact calculation requires more details.`);
    }

    // Debt and DTI insights
    const totalDebtWithMortgage = safeMonthlyDebts + safeMaxMonthlyPayment;
    const totalDebtPercentage = safeMonthlyIncome > 0 
      ? (totalDebtWithMortgage / safeMonthlyIncome) * 100 
      : 0;

    if (totalDebtPercentage > 50) {
      insights.push(`⚠️ WARNING: Your total monthly debt of ${formatCurrency(totalDebtWithMortgage)} (including potential mortgage payment and current debts of ${formatCurrency(safeMonthlyDebts)}) represents ${totalDebtPercentage.toFixed(0)}% of your monthly income, which is CRITICALLY HIGH and poses severe financial risk.`);
    } else if (totalDebtPercentage > 43) {
      insights.push(`⚠️ CAUTION: Your total monthly debt of ${formatCurrency(totalDebtWithMortgage)} (including potential mortgage payment and current debts of ${formatCurrency(safeMonthlyDebts)}) represents ${totalDebtPercentage.toFixed(0)}% of your monthly income, which is above recommended lending standards.`);
    } else if (totalDebtPercentage > 36) {
      insights.push(`NOTICE: Your total monthly debt of ${formatCurrency(totalDebtWithMortgage)} (including potential mortgage payment and current debts of ${formatCurrency(safeMonthlyDebts)}) represents ${totalDebtPercentage.toFixed(0)}% of your monthly income, which is at the upper limit of recommended debt levels.`);
    } else {
      insights.push(`GOOD: Your total monthly debt of ${formatCurrency(totalDebtWithMortgage)} (including potential mortgage payment and current debts of ${formatCurrency(safeMonthlyDebts)}) represents ${totalDebtPercentage.toFixed(0)}% of your monthly income, which is within healthy financial limits.`);
    }

    return insights;
  };

  const getDTILimits = (loanType: string) => {
    switch (loanType.toLowerCase()) {
      case 'conventional':
        return { 
          frontEndLimit: 0.36, // Traditional front-end limit
          backEndLimit: 0.50   // Increased to 50% as per attorney's advice
        };
      case 'fha':
        return { 
          frontEndLimit: 0.31, // Traditional FHA front-end limit
          backEndLimit: 0.50   // Increased to 50% as per attorney's advice
        };
      case 'va':
        return { 
          frontEndLimit: 0.41, // Traditional VA front-end limit
          backEndLimit: 0.55   // Up to 55% as specified
        };
      case 'jumbo':
        return { 
          frontEndLimit: 0.28, // More conservative front-end limit
          backEndLimit: 0.43   // Maintained at 43%
        };
      default:
        return { 
          frontEndLimit: 0.36, 
          backEndLimit: 0.50 
        };
    }
  };

  const onSubmit = (formData: FormData) => {
    const monthlyDebts = 
      (parseCurrencyInput(formData.carPayment) || 0) + 
      (parseCurrencyInput(formData.studentLoans) || 0) + 
      (parseCurrencyInput(formData.creditCards) || 0) + 
      (parseCurrencyInput(formData.otherDebts) || 0);

    // Get DTI limits based on loan type
    const { frontEndLimit, backEndLimit } = getDTILimits(formData.loanType);

    // Calculate maximum housing payment based on front-end DTI
    const monthlyIncome = parseCurrencyInput(formData.monthlyIncome);
    const maxHousingPayment = monthlyIncome * frontEndLimit;

    // Calculate back-end DTI (total debt ratio)
    const backEndDTI = (monthlyDebts + maxHousingPayment) / monthlyIncome;

    // Adjust maximum housing payment if back-end DTI exceeds limit
    let adjustedMaxHousingPayment = maxHousingPayment;
    if (backEndDTI > backEndLimit) {
      // Reduce housing payment to meet back-end DTI limit
      adjustedMaxHousingPayment = (backEndLimit * monthlyIncome) - monthlyDebts;
    }

    // Calculate maximum home price based on adjusted parameters
    const maxHomePrice = calculateMaxHomePrice(
      adjustedMaxHousingPayment,
      formData.loanType,
      formData.creditScore,
      {
        interestRate: formData.interestRate,
        loanTerm: formData.loanTerm,
        propertyTaxRate: formData.propertyTaxRate,
        homeInsurance: parseCurrencyInput(formData.homeInsurance),
        hoaFees: parseCurrencyInput(formData.hoaFees),
        monthlyDebts
      }
    );

    // Calculate debt reduction impact
    const debtImpact = calculateDebtImpact(
      monthlyDebts,
      monthlyIncome,
      adjustedMaxHousingPayment,
      formData.loanType,
      formData.creditScore,
      {
        interestRate: formData.interestRate,
        loanTerm: formData.loanTerm,
        propertyTaxRate: formData.propertyTaxRate,
        homeInsurance: parseCurrencyInput(formData.homeInsurance),
        hoaFees: parseCurrencyInput(formData.hoaFees)
      }
    );

    // Set results with more detailed information
    setResults({
      loanType: formData.loanType,
      monthlyIncome,
      monthlyDebts,
      maxMonthlyPayment: adjustedMaxHousingPayment,
      maxHomePrice,
      frontEndDTI: adjustedMaxHousingPayment / monthlyIncome,  
      backEndDTI: (monthlyDebts + adjustedMaxHousingPayment) / monthlyIncome,  
      ...debtImpact
    });
  };

  const handleCurrencyInput = (event: React.ChangeEvent<HTMLInputElement>, fieldName: keyof FormData) => {
    const rawValue = event.target.value;
    const formattedValue = formatCurrencyInput(rawValue);
    setValue(fieldName, formattedValue as any);
  };

  return (
    <div>
      <SEO 
        title="Illinois Maximum Home Purchasing Power Calculator | Mortgage Qualification"
        description="Calculate your maximum home purchase price in Illinois. Free tool to assess your home buying potential based on income, debts, and financial profile."
        keywords={["home buying power calculator", "maximum home price", "Illinois home buyers", "loan affordability", "home purchase potential"]}
        canonicalUrl="https://calculators.northsidelegal.com/dti-calculator"
        structuredData={{
          "@type": "Calculator",
          "name": "Maximum Home Purchasing Power Calculator",
          "description": "Estimate your maximum home purchase price based on financial factors",
          "url": "https://calculators.northsidelegal.com/dti-calculator",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "7321 Ridgeway Avenue",
            "addressLocality": "Skokie",
            "addressRegion": "IL",
            "postalCode": "60076",
            "addressCountry": "US"
          },
          "provider": {
            "@type": "LegalService",
            "name": "Adler & Herbach Real Estate Attorneys",
            "url": "https://calculators.northsidelegal.com",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "7321 Ridgeway Avenue",
              "addressLocality": "Skokie",
              "addressRegion": "IL",
              "postalCode": "60076",
              "addressCountry": "US"
            },
            "telephone": "+1 (847) 318-3770",
            "priceRange": "$750"
          }
        }}
      />
      {/* Add script tag for structured data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      <ContentWrapper>
        <div className="card">
          <div className="p-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">
              Purchasing Power Calculator
            </h1>
            <p className="mt-2 text-gray-500">
              Calculate your maximum home price based on your debt-to-income ratio.
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 divide-y divide-gray-200">
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center mt-8">
                    Income Information
                    <span className="tooltip ml-1">
                      <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                      <span className="tooltip-content -mt-1 ml-2">
                        Your monthly income is used to calculate your debt-to-income ratio
                      </span>
                    </span>
                  </h3>
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Monthly Income *
                      </label>
                      <div className="mt-1 relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                          $
                        </span>
                        <input
                          type="text"
                          {...register('monthlyIncome', { 
                            required: 'Monthly income is required',
                            validate: value => parseCurrencyInput(value) > 0 || 'Income must be greater than 0'
                          })}
                          onChange={(e) => handleCurrencyInput(e, 'monthlyIncome')}
                          placeholder="5,000"
                          className={`input-field pl-7 ${errors.monthlyIncome ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {errors.monthlyIncome && (
                        <p className="mt-2 text-sm text-red-600">{errors.monthlyIncome.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Credit Score
                        <span className="tooltip ml-1">
                          <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                          <span className="tooltip-content -mt-1 ml-2">
                            Your credit score affects your loan terms and maximum home price
                          </span>
                        </span>
                      </label>
                      <div className="mt-1 relative">
                        <input
                          type="number"
                          {...register('creditScore', { valueAsNumber: true })}
                          className="input-field"
                          placeholder="700"
                          min="300"
                          max="850"
                        />
                      </div>
                      {errors.creditScore && (
                        <p className="mt-2 text-sm text-red-600">{errors.creditScore.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center">
                    Monthly Debts
                    <span className="tooltip ml-1">
                      <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                      <span className="tooltip-content -mt-1 ml-2">
                        Include all monthly debt payments like car loans, student loans, and credit cards
                      </span>
                    </span>
                  </h3>
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Car Payment
                        <span className="tooltip ml-1">
                          <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                          <span className="tooltip-content -mt-1 ml-2">
                            Monthly car loan payment affects your maximum home price
                          </span>
                        </span>
                      </label>
                      <div className="mt-1 relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                          $
                        </span>
                        <input
                          type="text"
                          {...register('carPayment', { 
                            required: 'Car payment is required',
                            validate: value => parseCurrencyInput(value) >= 0 || 'Car payment cannot be negative'
                          })}
                          onChange={(e) => handleCurrencyInput(e, 'carPayment')}
                          placeholder="0"
                          className={`input-field pl-7 ${errors.carPayment ? 'border-red-500' : ''}`}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Student Loans
                        <span className="tooltip ml-1">
                          <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                          <span className="tooltip-content -mt-1 ml-2">
                            Monthly student loan payment impacts your debt-to-income ratio
                          </span>
                        </span>
                      </label>
                      <div className="mt-1 relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                          $
                        </span>
                        <input
                          type="text"
                          {...register('studentLoans', { 
                            required: 'Student loans amount is required',
                            validate: value => parseCurrencyInput(value) >= 0 || 'Student loans cannot be negative'
                          })}
                          onChange={(e) => handleCurrencyInput(e, 'studentLoans')}
                          placeholder="0"
                          className={`input-field pl-7 ${errors.studentLoans ? 'border-red-500' : ''}`}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Credit Card Minimum Payments
                        <span className="tooltip ml-1">
                          <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                          <span className="tooltip-content -mt-1 ml-2">
                            Monthly credit card minimum payments are factored into your loan qualification
                          </span>
                        </span>
                      </label>
                      <div className="mt-1 relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                          $
                        </span>
                        <input
                          type="text"
                          {...register('creditCards', { 
                            required: 'Credit card minimum payments are required',
                            validate: value => parseCurrencyInput(value) >= 0 || 'Credit card minimum payments cannot be negative'
                          })}
                          onChange={(e) => handleCurrencyInput(e, 'creditCards')}
                          placeholder="0"
                          className={`input-field pl-7 ${errors.creditCards ? 'border-red-500' : ''}`}
                        />
                      </div>
                      {errors.creditCards && (
                        <p className="mt-2 text-sm text-red-600">{errors.creditCards.message}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Other Debts
                        <span className="tooltip ml-1">
                          <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                          <span className="tooltip-content -mt-1 ml-2">
                            Additional monthly debt obligations that affect your borrowing power
                          </span>
                        </span>
                      </label>
                      <div className="mt-1 relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                          $
                        </span>
                        <input
                          type="text"
                          {...register('otherDebts', { 
                            required: 'Other debts amount is required',
                            validate: value => parseCurrencyInput(value) >= 0 || 'Other debts cannot be negative'
                          })}
                          onChange={(e) => handleCurrencyInput(e, 'otherDebts')}
                          placeholder="0"
                          className={`input-field pl-7 ${errors.otherDebts ? 'border-red-500' : ''}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center">
                    Property Details
                    <span className="tooltip ml-1">
                      <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                      <span className="tooltip-content -mt-1 ml-2">
                        These details help calculate your maximum home price
                      </span>
                    </span>
                  </h3>
                  <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Property Tax Rate (%)
                        <span className="tooltip ml-1">
                          <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                          <span className="tooltip-content -mt-1 ml-2">
                            Local property tax rate affects your maximum home price calculation
                          </span>
                        </span>
                      </label>
                      <div className="mt-1 relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                          %
                        </span>
                        <input
                          type="number"
                          step="0.01"
                          {...register('propertyTaxRate', { valueAsNumber: true })}
                          className="input-field pl-7"
                          placeholder="2"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Annual Home Insurance
                        <span className="tooltip ml-1">
                          <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                          <span className="tooltip-content -mt-1 ml-2">
                            Estimated annual home insurance cost impacts your loan affordability
                          </span>
                        </span>
                      </label>
                      <div className="mt-1 relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                          $
                        </span>
                        <input
                          type="text"
                          {...register('homeInsurance', { 
                            required: 'Annual home insurance is required',
                            validate: value => parseCurrencyInput(value) >= 0 || 'Home insurance cannot be negative'
                          })}
                          onChange={(e) => handleCurrencyInput(e, 'homeInsurance')}
                          placeholder="2,500"
                          className={`input-field pl-7 ${errors.homeInsurance ? 'border-red-500' : ''}`}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Monthly HOA Fees
                        <span className="tooltip ml-1">
                          <InformationCircleIcon className="h-4 w-4 inline text-gray-400" />
                          <span className="tooltip-content -mt-1 ml-2">
                            Monthly Homeowners Association fees are included in your debt-to-income calculation
                          </span>
                        </span>
                      </label>
                      <div className="mt-1 relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                          $
                        </span>
                        <input
                          type="text"
                          {...register('hoaFees', { 
                            required: 'Monthly HOA fees are required',
                            validate: value => parseCurrencyInput(value) >= 0 || 'HOA fees cannot be negative'
                          })}
                          onChange={(e) => handleCurrencyInput(e, 'hoaFees')}
                          placeholder="0"
                          className={`input-field pl-7 ${errors.hoaFees ? 'border-red-500' : ''}`}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Interest Rate (%)
                      </label>
                      <div className="mt-1 relative">
                        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">
                          %
                        </span>
                        <input
                          type="number"
                          step="0.1"
                          {...register('interestRate', { valueAsNumber: true })}
                          className="input-field pl-7"
                          placeholder="6.5"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Loan Term (Years)
                      </label>
                      <div className="mt-1">
                        <select
                          {...register('loanTerm', { valueAsNumber: true })}
                          className="input-field"
                        >
                          <option value={15}>15</option>
                          <option value={20}>20</option>
                          <option value={30}>30</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Loan Type
                      </label>
                      <div className="mt-1">
                        <select
                          {...register('loanType')}
                          className="input-field"
                        >
                          <option value="conventional">Conventional</option>
                          <option value="fha">FHA</option>
                          <option value="va">VA</option>
                          <option value="jumbo">Jumbo</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8">
                <div className="flex justify-center">
                  <button 
                    type="submit" 
                    disabled={!isValid}
                    className={`
                      btn-primary 
                      w-auto 
                      px-8 
                      ${!isValid ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'}
                      transition-transform 
                      duration-200 
                      ease-in-out
                    `}
                  >
                    Calculate Maximum Home Price
                  </button>
                </div>
              </div>
            </form>

            {results && (
              <div ref={resultsRef} className="mt-8">
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-6 py-5">
                    <h4 className="text-lg font-semibold text-gray-900">Maximum Home Price</h4>
                    <p className="mt-1 text-3xl font-bold text-brand-600">
                      {formatCurrency(results.maxHomePrice)}
                    </p>
                  </div>

                  <div className="border-t border-gray-200 px-6 py-4">
                    <h5 className="font-medium text-gray-900">Key Insights</h5>
                    <div className="mt-4 space-y-3">
                      {generateKeyInsights(results).map((insight, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            {insight.includes('WARNING') ? (
                              <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                            ) : insight.includes('Recommendation') ? (
                              <LightBulbIcon className="h-5 w-5 text-yellow-600" />
                            ) : insight.includes('Reducing') ? (
                              <ArrowTrendingDownIcon className="h-5 w-5 text-brand-600" />
                            ) : (
                              <InformationCircleIcon className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{insight}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-200 px-6 py-4">
                    <h5 className="font-medium text-gray-900">Ways to Improve Home Buying Power</h5>
                    <div className="mt-2 space-y-2">
                      <ul className="space-y-1 text-sm text-gray-600 list-disc list-inside">
                        <li>Increase your monthly income</li>
                        <li>Reduce your monthly debt payments</li>
                        <li>Save for a larger down payment</li>
                        <li>Look for lower interest rates</li>
                        <li>Consider a longer loan term</li>
                      </ul>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 px-6 py-4">
                    <h5 className="font-medium text-gray-900">DTI Ratios</h5>
                    <div className="mt-2 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Front-end DTI:</span>
                        <span className="font-medium text-gray-900">{(results.frontEndDTI * 100).toFixed(1)}%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Back-end DTI:</span>
                        <span className="font-medium text-gray-900">{(results.backEndDTI * 100).toFixed(1)}%</span>
                      </div>
                    </div>
                    
                    <div className="mt-6">
                      <Disclosure>
                        {({ open }) => (
                          <>
                            <Disclosure.Button className="flex w-full items-center justify-between px-6 py-4 bg-white rounded-lg shadow-sm hover:bg-gray-50 group transition-all duration-300 ease-in-out cursor-pointer">
                              <div className="flex items-center space-x-3 text-left">
                                <span className="font-medium text-brand-700 group-hover:text-brand-800">Understanding DTI Ratios</span>
                                <span className="text-xs bg-brand-50 text-brand-600 px-2 py-0.5 rounded-full group-hover:bg-brand-100 hidden sm:inline-block">
                                  Click to Expand
                                </span>
                              </div>
                              <ChevronDownIcon
                                className={`h-5 w-5 text-brand-500 transform transition-transform duration-300 ${
                                  open ? 'rotate-180' : ''
                                }`}
                              />
                            </Disclosure.Button>
                            <Disclosure.Panel className="mt-4 bg-brand-50 rounded-lg p-6">
                              <div className="space-y-6 text-left">
                                <div>
                                  <h6 className="text-lg font-medium text-brand-700">Front-end DTI (Housing Ratio)</h6>
                                  <p className="mt-3 text-sm leading-relaxed text-gray-600">
                                    This is the percentage of your monthly income that goes toward housing costs (mortgage, taxes, insurance). 
                                    Lenders typically prefer this to be under 28%. A lower front-end DTI means you can qualify for a larger home loan.
                                  </p>
                                </div>
                                <div>
                                  <h6 className="text-lg font-medium text-brand-700">Back-end DTI (Total Debt Ratio)</h6>
                                  <p className="mt-3 text-sm leading-relaxed text-gray-600">
                                    This includes ALL monthly debt payments (housing + other debts) as a percentage of your income. 
                                    Most lenders require this to be under 36-43%. A lower back-end DTI increases your maximum home price.
                                  </p>
                                </div>
                              </div>
                            </Disclosure.Panel>
                          </>
                        )}
                      </Disclosure>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </ContentWrapper>
    </div>
  )
}
